import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Button from '@/components-latest/primitives/Button'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import { Logout2 } from '@/components-latest/primitives/Icons'
import Text from '@/components-latest/primitives/Text'
import history from '@/config/router/history'
import { PATHS } from '@/config/router/routes'
import { DENIED_STORE_STATUSES } from '@/constants/common'
import { logout } from '@/ducks/auth/slice/auth.slice'
import { selectStoreStatus } from '@/ducks/store/selectors/store.selectors'

import Styles from './LoginDeniedPage.module.scss'

const LoginDeniedPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const contentByStatus = useMemo(
    () => ({
      '0': {
        imageUrl: '/images/account-deactivated.svg',
        title: t('login.denied.deactivated-title'),
        description: t('login.denied.deactivated-description')
      },
      '2': {
        imageUrl: '/images/account-suspended.svg',
        title: t('login.denied.suspended-title'),
        description: t('login.denied.suspended-description')
      },
      '3': {
        imageUrl: '/images/registration-in-progress.svg',
        title: t('login.denied.in-progress-title'),
        description: t('login.denied.in-progress-description')
      }
    }),
    [t]
  )

  const currentStoreStatus = useSelector(selectStoreStatus)
  const status = String(currentStoreStatus) as keyof typeof contentByStatus

  const imageUrl =
    contentByStatus?.[status]?.imageUrl || '/images/account-deactivated.svg'
  const title =
    contentByStatus?.[status]?.title || t('login.denied.placeholder-title')
  const description =
    contentByStatus?.[status]?.description ||
    t('login.denied.placeholder-description')

  const onLogout = () => {
    dispatch(logout())
  }

  useEffect(() => {
    if (!DENIED_STORE_STATUSES.includes(currentStoreStatus as number)) {
      history.push(PATHS.ORDERS)
    }
  }, [currentStoreStatus])

  if (!DENIED_STORE_STATUSES.includes(currentStoreStatus as number)) return null

  return (
    <div className={Styles.page}>
      <ContentBlock className={Styles.info}>
        <img src={imageUrl} alt="denied title" />
        <Text variant="title-1" weight="bold">
          {title}
        </Text>
        <Text>{description}</Text>
        <Button
          className={Styles.logout}
          size="large"
          variant="tertiary"
          onClick={onLogout}
          icon={<Logout2 />}>
          {t('login.denied.logout')}
        </Button>
      </ContentBlock>
    </div>
  )
}

export default LoginDeniedPage
